export const GEOCOMPLY_DOCUMENTED_CLIENT_ERROR_CODES = [
    0, 600, 602, 603, 604, 605, 609, 611, 614, 620, 630, 635, 639, 670, 671, 672, 673, 674,
] as const;

export type GeoComplyDocumentedClientErrorCode = (typeof GEOCOMPLY_DOCUMENTED_CLIENT_ERROR_CODES)[number];
export type GeoComplyClientErrorRecovery = 'success' | 'retry' | 'reauthorize' | 'wait-and-retry' | 'support';
export type GeoComplyClientErrorNoticeKind = 'location' | 'software' | 'connection' | 'waiting' | 'support';

export const GEOCOMPLY_VERIFICATION_INCOMPLETE_TITLE = "Location verification didn't finish";
export const GEOCOMPLY_RETRY_OR_SUPPORT_HELP = 'Try again. If this keeps happening, contact support.';
export const GEOCOMPLY_LOCATION_ACCESS_BLOCKED_TITLE = 'You need to verify your location to trade.';
export const GEOCOMPLY_LOCATION_ACCESS_BLOCKED_HELP =
    'Location access is blocked. Turn on location services and allow this site to access your location, then try again.';

export interface GeoComplyClientErrorPolicy {
    recovery: GeoComplyClientErrorRecovery;
    /** Whether to offer another user-triggered check after automatic recovery is exhausted. */
    canRetry: boolean;
    title: string;
    description: string;
    noticeKind?: GeoComplyClientErrorNoticeKind;
}

/**
 * Exhaustive policy for BrowserGuard's documented JWT geolocation response
 * codes. The `satisfies` clause makes a newly added documented code a compile
 * error until its recovery behavior and user guidance are chosen explicitly.
 */
export const GEOCOMPLY_CLIENT_ERROR_POLICIES = {
    0: {
        recovery: 'success',
        canRetry: true,
        title: 'Location verification returned an incomplete response',
        description: 'Try verifying your location again. If this keeps happening, contact support.',
    },
    600: {
        recovery: 'retry',
        canRetry: true,
        title: GEOCOMPLY_VERIFICATION_INCOMPLETE_TITLE,
        description: GEOCOMPLY_RETRY_OR_SUPPORT_HELP,
    },
    602: {
        recovery: 'retry',
        canRetry: true,
        title: 'Check your internet connection',
        description: 'Reconnect to the internet, then try verifying your location again.',
        noticeKind: 'connection',
    },
    603: {
        recovery: 'retry',
        canRetry: true,
        title: 'Location service is unavailable',
        description: "GeoComply couldn't be reached. Wait a moment, then try again.",
        noticeKind: 'connection',
    },
    604: {
        recovery: 'support',
        canRetry: false,
        title: 'Location verification is unavailable',
        description: 'The location verification account is suspended. Contact support for help.',
        noticeKind: 'support',
    },
    605: {
        recovery: 'support',
        canRetry: false,
        title: 'Location verification is unavailable',
        description: 'Location verification is disabled. Contact support for help.',
        noticeKind: 'support',
    },
    609: {
        recovery: 'support',
        canRetry: false,
        title: 'Location verification is misconfigured',
        description: 'The location request was configured incorrectly. Contact support for help.',
        noticeKind: 'support',
    },
    611: {
        recovery: 'retry',
        canRetry: true,
        title: GEOCOMPLY_LOCATION_ACCESS_BLOCKED_TITLE,
        description: GEOCOMPLY_LOCATION_ACCESS_BLOCKED_HELP,
        noticeKind: 'location',
    },
    614: {
        recovery: 'wait-and-retry',
        canRetry: false,
        title: 'Location check already in progress',
        description: 'Another location check is still running. Wait for it to finish, then refresh the page if needed.',
        noticeKind: 'waiting',
    },
    620: {
        recovery: 'retry',
        canRetry: true,
        title: 'Location service returned an invalid response',
        description: GEOCOMPLY_RETRY_OR_SUPPORT_HELP,
    },
    630: {
        recovery: 'retry',
        canRetry: true,
        title: 'Location information expired',
        description: 'Try again to collect fresh location information.',
        noticeKind: 'location',
    },
    635: {
        recovery: 'support',
        canRetry: false,
        title: 'Location request is invalid',
        description: 'The location request could not be processed. Contact support for help.',
        noticeKind: 'support',
    },
    639: {
        recovery: 'support',
        canRetry: false,
        title: 'Location verification is misconfigured',
        description: 'The request is missing its reason code. Contact support for help.',
        noticeKind: 'support',
    },
    670: {
        recovery: 'reauthorize',
        canRetry: false,
        title: "Location session couldn't be refreshed",
        description: 'Refresh the page. If this keeps happening, contact support.',
        noticeKind: 'support',
    },
    671: {
        recovery: 'reauthorize',
        canRetry: false,
        title: "Location session couldn't be refreshed",
        description: 'Refresh the page. If this keeps happening, contact support.',
        noticeKind: 'support',
    },
    672: {
        recovery: 'support',
        canRetry: false,
        title: 'Location verification is misconfigured',
        description: 'The location signing key is not registered. Contact support for help.',
        noticeKind: 'support',
    },
    673: {
        recovery: 'support',
        canRetry: false,
        title: 'Location service configuration unavailable',
        description: "GeoComply's configuration couldn't be loaded. Refresh the page or contact support for help.",
        noticeKind: 'support',
    },
    674: {
        recovery: 'reauthorize',
        canRetry: false,
        title: "Location session couldn't be refreshed",
        description: 'Refresh the page. If this keeps happening, contact support.',
        noticeKind: 'support',
    },
} as const satisfies Record<GeoComplyDocumentedClientErrorCode, GeoComplyClientErrorPolicy>;

const LEGACY_REAUTHORIZE_ERROR_CODES = new Set([607, 608]);

export function getGeoComplyClientErrorPolicy(
    errorCode: number | null | undefined,
): GeoComplyClientErrorPolicy | undefined {
    if (errorCode == null) return undefined;
    return GEOCOMPLY_CLIENT_ERROR_POLICIES[errorCode as GeoComplyDocumentedClientErrorCode];
}

export function shouldReauthorizeAfterGeoComplyError(errorCode: number | undefined): boolean {
    const policy = getGeoComplyClientErrorPolicy(errorCode);
    return policy?.recovery === 'reauthorize' || (errorCode != null && LEGACY_REAUTHORIZE_ERROR_CODES.has(errorCode));
}

export function shouldWaitAndRetryGeoComplyError(errorCode: number | undefined): boolean {
    return getGeoComplyClientErrorPolicy(errorCode)?.recovery === 'wait-and-retry';
}
